import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Brain, Puzzle, Users, Target, ArrowRight, GraduationCap, RefreshCcw, KeyRound, CheckCircle2 } from "lucide-react";
import { useSite, money } from "@/lib/site";
import Navbar from "@/components/Navbar";
import { SiteFooter } from "@/components/Brand";
import { AccessCodeForm } from "@/components/AccessCodeForm";
import { Button } from "@/components/ui/button";

const pillars = [
  { icon: Brain, title: "Cognitive Ability", color: "#EF4444", desc: "Reasoning, logic & problem-solving power." },
  { icon: Puzzle, title: "Aptitude", color: "#F59E0B", desc: "Numerical, verbal, abstract & spatial skills." },
  { icon: Users, title: "DISC Personality", color: "#10B981", desc: "How you work, lead and communicate." },
  { icon: Target, title: "Holland Interests", color: "#3B82F6", desc: "The RIASEC code that energizes you." },
];

const TrackCard = ({ icon: Icon, name, who, bullets, track, price, ready, delay, testId }) => {
  const navigate = useNavigate();
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay }}
      className="bg-white rounded-3xl border border-slate-200 p-7 flex flex-col hover:shadow-xl transition-shadow" data-testid={testId}>
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center"><Icon className="w-6 h-6 text-primary" /></div>
        <div><p className="font-body text-xs uppercase tracking-[0.2em] text-primary">CareerCompass</p><h3 className="font-heading font-extrabold text-2xl text-slate-900">{name}</h3></div>
      </div>
      <p className="font-body text-sm text-slate-500 mt-4">{who}</p>
      <ul className="space-y-2 mt-4 mb-6">
        {bullets.map((b) => <li key={b} className="font-body text-sm text-slate-700 flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" /> {b}</li>)}
      </ul>
      <div className="mt-auto flex items-center justify-between gap-3 pt-4 border-t border-slate-100">
        <p className="font-heading font-extrabold text-xl text-slate-900">{price}</p>
        <Button onClick={() => navigate(`/checkout?type=assessment&track=${track}`)} disabled={!ready} className="rounded-full font-heading font-bold" data-testid={`${testId}-buy`}>
          Get access via UPI <ArrowRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </motion.div>
  );
};

const AssessmentsPage = () => {
  const site = useSite();
  const price = site.assessment_price ? money(site.assessment_price, site.currency) : "";
  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navbar />
      <section className="bg-[#0B2F55]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 grid lg:grid-cols-[1.1fr_0.9fr] gap-10 items-center">
          <div>
            <p className="font-body text-xs uppercase tracking-[0.2em] text-sky-300">Career assessments</p>
            <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl text-[#F5F1E8] mt-3 leading-[1.05]" data-testid="assessments-title">Find the career that <span className="text-sky-300">actually fits you.</span></h1>
            <p className="font-body text-base md:text-lg text-[#F5F1E8]/75 mt-6 max-w-xl">Two tracks, one science-backed assessment. Cognition, aptitude, DISC personality and Holland interests, mapped into a structured AI-written career alignment report in about 25 minutes.</p>
          </div>
          <div className="bg-white rounded-3xl p-7 shadow-2xl" data-testid="assessments-code-card">
            <div className="flex items-center gap-2 mb-1"><KeyRound className="w-5 h-5 text-primary" /><h2 className="font-heading font-bold text-lg text-slate-900">Already have an access code?</h2></div>
            <p className="font-body text-sm text-slate-500 mb-5">Enter the code and one-time password you received to start or resume.</p>
            <AccessCodeForm />
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20" data-testid="tracks-section">
        <h2 className="font-heading font-extrabold text-2xl sm:text-3xl text-slate-900">Pick your track</h2>
        <p className="font-body text-base text-slate-600 mt-2 max-w-2xl">Same rigorous assessment, two very different reports — each written for where you are right now.</p>
        {!site.payments_ready && site.upi_id !== undefined && (
          <p className="font-body text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mt-6 inline-block" data-testid="assessment-pay-unavailable">Direct purchase is opening soon. Codes are currently issued through our partners and counsellors.</p>
        )}
        <div className="grid md:grid-cols-2 gap-6 mt-8">
          <TrackCard icon={GraduationCap} name="Start" track="start" price={price} ready={site.payments_ready} delay={0} testId="track-start"
            who="For students and first-jobbers choosing a stream, degree or first role."
            bullets={["Starting strengths & course recommendations", "Entry routes into matched careers", "Exposure ideas and a 12-month plan", "A section for parents & counsellors"]} />
          <TrackCard icon={RefreshCcw} name="Comeback" track="comeback" price={price} ready={site.payments_ready} delay={0.1} testId="track-comeback"
            who="For career changers and anyone returning to work after a break."
            bullets={["Transferable strengths, honestly assessed", "5 career matches with fit scores", "Development areas & confidence roadmap", "Your next 90 days, step by step"]} />
        </div>
      </section>

      <section className="bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
          <h2 className="font-heading font-extrabold text-2xl sm:text-3xl text-slate-900 mb-3">Four lenses. One clear direction.</h2>
          <p className="font-body text-base text-slate-600 mb-10 max-w-2xl">Every assessment blends four proven frameworks so your results reflect the whole you.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {pillars.map((p, i) => (
              <motion.div key={p.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                className="rounded-2xl border border-slate-200 p-6 hover:-translate-y-1 transition-transform" data-testid={`pillar-${i}`}>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ background: p.color + "1a" }}><p.icon className="w-6 h-6" style={{ color: p.color }} /></div>
                <h3 className="font-heading font-bold text-lg text-slate-900">{p.title}</h3>
                <p className="font-body text-sm text-slate-500 mt-2">{p.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      <SiteFooter />
    </div>
  );
};

export default AssessmentsPage;
